import React from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/LanguageSwitcher';

interface FooterLink {
  label: string;
  href: string;
  isExternal?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface LandingFooterProps {
  sections?: FooterSection[];
  showFullFooter?: boolean;
}

/**
 * Shared landing page footer component.
 * Uses the main design system for consistent styling across all landing pages.
 */
export const LandingFooter: React.FC<LandingFooterProps> = ({
  sections,
  showFullFooter = true,
}) => {
  const { t } = useTranslation(['landing', 'common']);

  // Default footer sections
  const defaultSections: FooterSection[] = [
    {
      title: t('landing:footer.product.title'),
      links: [
        { label: t('landing:footer.product.features'), href: '#use-cases' },
        { label: t('landing:footer.product.pricing'), href: '#pricing' },
        { label: t('landing:footer.product.demo'), href: '#product' },
      ],
    },
    {
      title: t('landing:footer.company.title'),
      links: [
        { label: t('landing:footer.company.about'), href: '/about' },
        { label: t('landing:footer.company.contact'), href: '/contact' },
      ],
    },
    {
      title: t('landing:footer.resources.title'),
      links: [
        { label: t('landing:footer.resources.documentation'), href: 'https://docs.callcov.com', isExternal: true },
        { label: t('landing:footer.resources.apiReference'), href: 'https://docs.callcov.com/api/overview', isExternal: true },
        { label: t('landing:footer.resources.support'), href: '/support' },
      ],
    },
    {
      title: t('landing:footer.legal.title'),
      links: [
        { label: t('landing:footer.legal.privacy'), href: '/privacy' },
        { label: t('landing:footer.legal.terms'), href: '/terms' },
      ],
    },
  ];

  const footerSections = sections || defaultSections;

  if (!showFullFooter) {
    // Simple footer for product landing pages
    return (
      <footer className="bg-neutral-900">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-neutral-400 mb-4 md:mb-0">
              {t('landing:footer.copyright')}
            </p>
            <div className="flex gap-x-6 items-center">
              <Link to="/privacy" className="text-sm text-neutral-400 hover:text-white transition-colors">
                {t('landing:footer.legal.privacy')}
              </Link>
              <Link to="/terms" className="text-sm text-neutral-400 hover:text-white transition-colors">
                {t('landing:footer.legal.terms')}
              </Link>
              <Link to="/contact" className="text-sm text-neutral-400 hover:text-white transition-colors">
                {t('landing:footer.company.contact')}
              </Link>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-neutral-900 text-white py-16">
      <div className="container-custom">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="font-semibold mb-4">{section.title}</h3>
              <ul className="space-y-2 text-sm text-neutral-400">
                {section.links.map((link) =>
                  link.isExternal ? (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white transition-colors"
                      >
                        {link.label}
                      </a>
                    </li>
                  ) : link.href.startsWith('#') ? (
                    <li key={link.href}>
                      <a href={link.href} className="hover:text-white transition-colors">
                        {link.label}
                      </a>
                    </li>
                  ) : (
                    <li key={link.href}>
                      <Link to={link.href} className="hover:text-white transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  )
                )}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-neutral-800 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-neutral-400">
            {t('landing:footer.copyright')}
          </p>
          <div className="flex gap-6 mt-4 md:mt-0">
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default LandingFooter;
